import { TZDate } from '@date-fns/tz';
import type { TvChannel, TvChannelShowtime } from '@lib/graphql/generated';
import { parse } from 'date-fns';

type ShowtimeWatchData = Pick<TvChannelShowtime, 'start' | 'stop' | 'broadcastLinks' | 'catalogLinks'> & {
  channel?: Pick<TvChannel, 'joyn_livestream'> | null;
};

function validWatchUrl(value?: string | null): string | null {
  if (!value?.trim()) return null;
  const link = value.trim();
  try {
    const url = new URL(link);
    return ['https:', 'http:'].includes(url.protocol) && url.hostname ? link : null;
  } catch {
    return null;
  }
}

/** The EPG supplies Austrian local times without a UTC offset. */
function showtimeTimestamp(value: string): number {
  return parse(value, 'yyyy-MM-dd HH:mm:ss', new TZDate(0, 'Europe/Vienna')).getTime();
}

/** Shared by programme indicators and the detail CTA so they always agree. */
export function getShowtimeWatchUrl(
  showtime: ShowtimeWatchData | null | undefined,
  currentTime: Date | number,
  channel?: Pick<TvChannel, 'joyn_livestream'> | null,
): string | null {
  if (!showtime?.start || !showtime.stop) return null;

  const start = showtimeTimestamp(showtime.start);
  const stop = showtimeTimestamp(showtime.stop);
  const now = currentTime instanceof Date ? currentTime.getTime() : currentTime;
  if (!Number.isFinite(start) || !Number.isFinite(stop) || !Number.isFinite(now) || stop <= start || now < start) {
    return null;
  }

  if (now < stop) {
    return validWatchUrl(showtime.channel?.joyn_livestream) ?? validWatchUrl(channel?.joyn_livestream);
  }

  // Exact airing matches take precedence; the catalog also covers repeats.
  for (const offer of [...(showtime.broadcastLinks ?? []), ...(showtime.catalogLinks ?? [])]) {
    if (offer?.provider !== 'joyn') continue;
    const link = validWatchUrl(offer.link);
    if (link) return link;
  }
  return null;
}
